import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import EntityDetailDrawer from '../EntityDetailDrawer';
import { Section, Field } from '../Ontology/DetailPrimitives';
import { TierPill } from '../Benchmark/BenchmarkPrimitives';
import {
    CaseRow, CaseWorkflowQuery, TimelineResponse, caseWorkflowService
} from '../../services/caseWorkflowService';
import { transitionLabel } from './caseWorkflowVocabulary';
import { dsVar, ink } from '../../theme/tokens';

interface CaseTimelineDrawerProps {
    open: boolean;
    row: CaseRow | null;
    query: CaseWorkflowQuery;
    onClose: () => void;
}

const when = (iso: string) => new Date(iso).toLocaleString();

/**
 * One case's whole path, across its alert, context and report rows.
 *
 * This is where the transition log stops being a chart and becomes an audit
 * trail, so each step shows the tier that was in force AT THE TIME rather than
 * the one the record carries today.
 *
 * Docked rather than overlaid, so the list it came from stays on screen with
 * its filters and page intact while a reader works through cases.
 */
const CaseTimelineDrawer: React.FC<CaseTimelineDrawerProps> = ({ open, row, query, onClose }) => {
    const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<number | null>(null);

    useEffect(() => {
        if (!open || !row) return;
        let cancelled = false;
        setTimeline(null);
        setError(null);
        setExpanded(null);
        caseWorkflowService
            .getCaseTimeline(row.caseId, {
                project_id: query.project_id,
                since: query.since,
                until: query.until,
                // A record with no case id is addressed by its path instead.
                // Both halves are required: record_type alone would widen the
                // timeline to every record of that type in the project.
                ...(row.caseId ? {} : { record_type: row.recordType, record_key: row.recordKey })
            })
            .then((res) => { if (!cancelled) setTimeline(res); })
            .catch((err) => { if (!cancelled) setError(err?.message || 'Could not load this case.'); });
        return () => { cancelled = true; };
    }, [open, row, query.project_id, query.since, query.until]);

    return (
        <EntityDetailDrawer
            open={open}
            onClose={onClose}
            docked
            dockedMinWidth={1280}
            icon={<TimelineIcon />}
            subtitle={row?.customerName || row?.customerId || 'Case'}
            title={row?.caseId || row?.recordKey || ''}
            description={
                row
                    ? `Every step this case took, in order. The tier shown against each step is the one that was in force when it happened, not the one the case carries now.`
                    : ''
            }
        >
            {error && (
                <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5') }}>{error}</Typography>
            )}

            {!error && !timeline && (
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>Loading…</Typography>
            )}

            {timeline && (
                <>
                    <Section title="Now">
                        <Field label="State" value={String(row?.workflowState || '—').replace(/_/g, ' ')} />
                        <Field
                            label="Priority"
                            value={row?.priority ? <TierPill code={row.priority} /> : 'Not yet scored'}
                        />
                        {row?.machinePriority && row.machinePriority !== row.priority && (
                            <Field
                                label="Machine tier"
                                value={<><TierPill code={row.machinePriority} /> <span style={{ color: dsVar('fg-2'), fontSize: '0.75rem' }}>— a reviewer changed this</span></>}
                            />
                        )}
                    </Section>

                    <Section title={`Path (${timeline.pagination.totalRecords} steps, ${timeline.events} events)`}>
                        <Box className="flex flex-col gap-3">
                            {timeline.steps.map((step, index) => (
                                <Box
                                    key={`${step.transitionCode}-${step.firstAt}-${index}`}
                                    className="border-l-2 border-line pl-3"
                                >
                                    <Box className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-body'), fontWeight: 600 }}>
                                            {transitionLabel(step.transitionCode)}
                                        </Typography>
                                        {/*
                                          * A case is a customer, so a step can stand for many
                                          * identical events — 141 on a measured case. They are
                                          * collapsed and counted rather than listed.
                                          */}
                                        {step.count > 1 && (
                                            <Typography
                                                component="button"
                                                onClick={() => setExpanded(expanded === index ? null : index)}
                                                sx={{
                                                    fontSize: '0.6875rem', color: dsVar('accent-fg'), background: 'none',
                                                    border: 'none', cursor: 'pointer', padding: 0
                                                }}
                                            >
                                                ×{step.count} {expanded === index ? '(hide)' : '(show records)'}
                                            </Typography>
                                        )}
                                        {step.priority && <TierPill code={step.priority} />}
                                    </Box>
                                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                        {String(step.state).replace(/_/g, ' ')} · {when(step.firstAt)}
                                        {step.count > 1 && ` → ${when(step.lastAt)}`}
                                    </Typography>
                                    {expanded === index && (
                                        <Box className="mt-1 flex flex-wrap gap-1">
                                            {step.recordKeys.map((key) => (
                                                <Typography
                                                    key={key}
                                                    sx={{
                                                        fontSize: '0.625rem', color: dsVar('fg-2'), fontFamily: '"JetBrains Mono", monospace',
                                                        backgroundColor: dsVar('page'), px: 0.75, py: 0.25, borderRadius: '4px'
                                                    }}
                                                >
                                                    {key}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Section>
                </>
            )}
        </EntityDetailDrawer>
    );
};

export default CaseTimelineDrawer;
